import { FormEvent, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import {
  createTicketLabel,
  getApiErrorMessage,
  getTicketLabel,
  updateTicketLabelDefinition,
} from '../../api/client';
import { useAuth } from '../../contexts/AuthContext';
import { canEditPlatformConfig, canWritePlatformConfig } from '../../utils/permissions';
import { PLATFORM_CONFIG } from '../platform-config/paths';

export default function TicketLabelFormPage() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === 'new';
  const navigate = useNavigate();
  const { user } = useAuth();

  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [color, setColor] = useState('');
  const [sortOrder, setSortOrder] = useState(0);
  const [isActive, setIsActive] = useState(true);
  const [isDefault, setIsDefault] = useState(false);
  const [isSystem, setIsSystem] = useState(false);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const canEdit =
    user && (isNew ? canWritePlatformConfig(user) : canEditPlatformConfig(user));

  useEffect(() => {
    if (isNew || !id) return;
    getTicketLabel(id)
      .then((l) => {
        setCode(l.code);
        setName(l.name);
        setDescription(l.description ?? '');
        setColor(l.color ?? '');
        setSortOrder(l.sortOrder);
        setIsActive(l.isActive);
        setIsDefault(l.isDefault);
        setIsSystem(l.isSystem);
      })
      .catch((err) => setError(getApiErrorMessage(err)))
      .finally(() => setLoading(false));
  }, [id, isNew]);

  async function handleSave(e: FormEvent) {
    e.preventDefault();
    if (!canEdit) return;
    setSaving(true);
    setError('');
    try {
      if (isNew) {
        await createTicketLabel({
          code: code.trim(),
          name: name.trim(),
          description: description.trim() || undefined,
          color: color.trim() || undefined,
          sortOrder,
          isActive,
          isDefault,
        });
        navigate(PLATFORM_CONFIG.labels);
      } else if (id) {
        await updateTicketLabelDefinition(id, {
          name: name.trim(),
          description: description.trim() || null,
          color: color.trim() || null,
          sortOrder,
          isActive,
          isDefault,
        });
        navigate(PLATFORM_CONFIG.labels);
      }
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  if (!canEdit) return <div className="alert alert-error">No permission.</div>;
  if (loading) return <div className="loading">Loading…</div>;

  return (
    <>
      <div className="page-header">
        <Link to={PLATFORM_CONFIG.labels} style={{ fontSize: '0.85rem' }}>← Labels</Link>
        <h1>{isNew ? 'New label' : 'Edit label'}</h1>
      </div>
      {error && <div className="alert alert-error">{error}</div>}
      <form className="card card-body" style={{ maxWidth: 560 }} onSubmit={handleSave}>
        <div className="form-group">
          <label>Code</label>
          <input className="form-control" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} required disabled={!isNew} />
        </div>
        <div className="form-group">
          <label>Name</label>
          <input className="form-control" value={name} onChange={(e) => setName(e.target.value)} required />
        </div>
        <div className="form-group">
          <label>Description</label>
          <textarea className="form-control" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
        </div>
        <div className="form-group">
          <label>Color (optional)</label>
          <input className="form-control" value={color} onChange={(e) => setColor(e.target.value)} placeholder="#3B82F6" />
        </div>
        <div className="form-group">
          <label>Sort order</label>
          <input type="number" min={0} className="form-control" style={{ maxWidth: 120 }} value={sortOrder} onChange={(e) => setSortOrder(Number(e.target.value))} />
        </div>
        <label>
          <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} /> Active
        </label>
        <label style={{ display: 'block', marginTop: '0.5rem' }}>
          <input type="checkbox" checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} /> Default label
        </label>
        {isSystem && (
          <small style={{ display: 'block', marginTop: '0.35rem', color: 'var(--text-muted)' }}>
            Seeded system label — you can still rename, deactivate, or change the default.
          </small>
        )}
        <button type="submit" className="btn btn-primary" disabled={saving} style={{ marginTop: '1rem' }}>
          {saving ? 'Saving…' : 'Save'}
        </button>
      </form>
    </>
  );
}
